// ============================================================================
// VerdictShareCard — 1080×1080 share-ready visual for a single dashboard
// verdict. Uses the same multi-channel share pattern as the platform's
// ShareAnalysisCard (X / Instagram / TikTok / WhatsApp / Copy / Download).
//
// Design language: v3 2026 (cyan/purple, mono uppercase eyebrows,
// gradient text, glass surfaces). Built so a user can tap one button
// from the dashboard and post a perfectly branded square to socials in
// under 5 seconds. This is the share-loop engine.
// ============================================================================
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import html2canvas from 'html2canvas';
import SafeIcon from './SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { FaInstagram, FaTiktok, FaWhatsapp } from 'react-icons/fa';
import { useShareCode } from '@/hooks/useShareCode';
import { supabase } from '@/integrations/supabase/client';
import logoSrc from '@/assets/snap-chart-logo-final.png';

const { FiX, FiDownload, FiCopy, FiCheck, FiShare2, FiImage } = FiIcons;

const TONE_MAP = {
  purple:  { hex: '#a855f7', glow: 'rgba(168,85,247,0.30)', label: 'NO-TRADE' },
  amber:   { hex: '#f59e0b', glow: 'rgba(245,158,11,0.30)', label: 'WAIT' },
  emerald: { hex: '#10b981', glow: 'rgba(16,185,129,0.30)', label: 'TRADE' },
  gray:    { hex: '#9ca3af', glow: 'rgba(156,163,175,0.20)', label: 'PENDING' },
};

const fmtPrice = (p) => {
  if (p === null || p === undefined) return '—';
  const n = Number(p); if (!Number.isFinite(n)) return '—';
  return `$${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};

const fmtVerdict = (v) => (v || 'PENDING').replace('_', '-').toUpperCase();

const APP_URL = (typeof window !== 'undefined' && window.location?.origin)
  || 'https://snap-chart.ai';

const VerdictShareCard = ({ verdict, onClose, userHandle = 'snapchartai' }) => {
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [igHint, setIgHint] = useState(false);
  // Per-user affiliate ref code — every share attributes back to this user
  const { code: shareCode } = useShareCode();

  // Convert the logo PNG to a base64 data URI on mount. html2canvas can be
  // flaky with bundled <img> sources (renders a broken-image fallback if
  // the file is mid-stream during capture). Embedding as a data URI sidesteps
  // all loading + CORS issues — the image is inline in the DOM, no network.
  const [logoDataUri, setLogoDataUri] = useState(logoSrc);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(logoSrc);
        const blob = await res.blob();
        const dataUri = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
        if (!cancelled && typeof dataUri === 'string') setLogoDataUri(dataUri);
      } catch (err) {
        console.warn('[VerdictShareCard] logo dataURI conversion failed:', err?.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!verdict) return null;
  const tone = TONE_MAP[verdict._tone] || TONE_MAP.gray;
  const v = fmtVerdict(verdict.verdict);
  // Use the user's real share_code so shares are tracked (not the generic 'share')
  const refUrl = `${APP_URL}/?ref=${shareCode || 'share'}`;

  // The caption shared alongside the image on X / Telegram / etc.
  const caption = [
    `${verdict.asset || '—'} → ${v}${verdict.conf ? ` (${verdict.conf}% conf)` : ''}`,
    '',
    v === 'NO-TRADE'
      ? 'My AI Risk Officer just vetoed this trade. Capital protected.'
      : v === 'WAIT'
      ? 'My AI Risk Officer says wait. Patience > FOMO.'
      : v === 'TRADE'
      ? 'My AI Risk Officer cleared this trade. Setup confirmed.'
      : 'AI verdict on this chart.',
    '',
    'The AI that tells you when NOT to trade.',
    refUrl,
    '',
    `#${(verdict.asset || 'trading').replace(/[^a-z0-9]/gi, '')} #SnapChartAI #TradingDiscipline`,
  ].join('\n');

  const fileName = `snapchart-${(verdict.asset || 'verdict').toLowerCase().replace(/[^a-z0-9]/g, '')}-${Date.now()}.png`;

  const generateBlob = useCallback(async () => {
    if (!cardRef.current) return null;
    setBusy(true);
    try {
      // CRITICAL: wait for every <img> inside the card to actually finish
      // loading. html2canvas captures synchronously — if any img is still
      // streaming, it gets rendered as a broken-image fallback (which is
      // exactly the empty shield outline the user saw instead of the real
      // SnapChart logo).
      const imgs = Array.from(cardRef.current.querySelectorAll('img'));
      await Promise.all(imgs.map((img) => {
        if (img.complete && img.naturalWidth > 0) return Promise.resolve();
        return new Promise((resolve) => {
          img.onload = resolve;
          img.onerror = resolve; // resolve anyway so we never hang
          // Re-trigger src to force a load attempt if it hadn't started
          if (!img.src) img.src = img.getAttribute('data-src') || img.src;
        });
      }));
      // Tiny defer so the browser flushes the layout after image load before capture
      await new Promise((r) => setTimeout(r, 50));

      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: '#0a0e1a',
        scale: 3,           // 3x for crisp Retina + Instagram quality
        useCORS: true,
        allowTaint: false,
        logging: false,
        imageTimeout: 5000, // give bundled assets up to 5s to decode
      });
      return new Promise((resolve) => canvas.toBlob((b) => resolve(b), 'image/png', 0.96));
    } finally {
      setBusy(false);
    }
  }, []);

  // ── Auto-upload to Supabase Storage on mount ──────────────────────
  // Every time this modal opens, generate the card once and quietly
  // upload it to `verdict-cards/{user_id}/{asset}.png`. The Twitter
  // Growth Agent's daily cron then serves that PNG at
  // /api/twitter-verdict-card so tweeted cards look byte-identical
  // to what the user sees here. Fire-and-forget — never blocks the UI
  // and never surfaces errors to the user.
  //
  // Ordered dependency: waits for logoDataUri to flip from the raw
  // bundled asset src to the resolved base64 URI, so the captured PNG
  // includes the real logo instead of a broken-image fallback.
  const [autoUploaded, setAutoUploaded] = useState(false);
  useEffect(() => {
    if (autoUploaded || !verdict?.asset) return;
    // Only run once per open. If logoDataUri hasn't converted yet,
    // wait for the next tick (this effect re-runs when it does).
    if (typeof logoDataUri !== 'string' || !logoDataUri.startsWith('data:')) return;

    let cancelled = false;
    // Small tick to let the DOM paint the card fully before capture.
    const t = setTimeout(async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user || cancelled) return;
        const blob = await generateBlob();
        if (!blob || cancelled) return;
        const assetKey = String(verdict.asset || 'UNKNOWN').toUpperCase().replace(/[^A-Z0-9]/g, '');
        const path = `${user.id}/${assetKey}.png`;
        const { error } = await supabase.storage
          .from('verdict-cards')
          .upload(path, blob, {
            contentType: 'image/png',
            upsert: true,          // overwrite prior card of same asset
            cacheControl: '300',   // 5 min — matches endpoint cache
          });
        if (error) {
          console.warn('[VerdictShareCard] auto-upload skipped:', error.message);
        } else {
          setAutoUploaded(true);
        }
      } catch (err) {
        console.warn('[VerdictShareCard] auto-upload error:', err?.message);
      }
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [verdict?.asset, logoDataUri, autoUploaded, generateBlob]);

  const download = async () => {
    const blob = await generateBlob();
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = fileName; a.click();
    URL.revokeObjectURL(url);
  };

  const nativeShare = async () => {
    const blob = await generateBlob();
    if (!blob) return;
    const file = new File([blob], fileName, { type: 'image/png' });
    try {
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: 'SnapChart AI verdict', text: caption });
        return;
      }
    } catch (e) { if (e?.name === 'AbortError') return; }
    // Fallback: download + X intent
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = fileName; a.click();
    URL.revokeObjectURL(url);
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(caption)}`, '_blank', 'noopener');
  };

  const copyImage = async () => {
    try {
      const blob = await generateBlob();
      if (!blob || !navigator.clipboard?.write) { await download(); return; }
      await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': blob })]);
      setCopied(true); setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.warn('[share] copy image failed:', err?.message);
      await download();
    }
  };

  const postOnX = async () => {
    await download();
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(caption)}`, '_blank', 'noopener');
  };

  const postOnInstagram = async () => {
    try { if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(caption); } catch { /* noop */ }
    await download();
    setIgHint(true); setTimeout(() => setIgHint(false), 4500);
    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || '');
    if (isMobile) {
      window.location.href = 'instagram://camera';
      setTimeout(() => { window.open('https://www.instagram.com', '_blank', 'noopener'); }, 800);
    } else {
      window.open('https://www.instagram.com', '_blank', 'noopener');
    }
  };

  const postOnTiktok = async () => {
    try { if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(caption); } catch { /* noop */ }
    await download();
    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || '');
    if (isMobile) window.location.href = 'snssdk1233://';
    setTimeout(() => { window.open('https://www.tiktok.com/upload', '_blank', 'noopener'); }, 500);
  };

  const postOnWhatsapp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(caption)}`, '_blank', 'noopener');
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-xl flex items-center justify-center p-4 overflow-y-auto"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.96, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.96, opacity: 0 }}
          className="relative w-full max-w-md my-6"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-cyan-400 mb-0.5">
                share verdict · 1080×1080
              </div>
              <h3 className="text-xl font-bold text-white leading-tight">
                Ready for <span className="bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">IG · TikTok · X</span>
              </h3>
            </div>
            <button onClick={onClose} aria-label="Close"
              className="w-10 h-10 rounded-lg border border-white/10 text-gray-400 hover:text-white hover:border-white/30 transition flex items-center justify-center">
              <SafeIcon icon={FiX} className="text-lg" />
            </button>
          </div>

          {/* The 1080×1080 share card (rendered at 360px on screen, captured at 3x) */}
          <div className="rounded-2xl overflow-hidden border border-white/10 mb-4 shadow-2xl shadow-cyan-500/10 flex justify-center">
            <div
              ref={cardRef}
              style={{
                width: '360px',
                height: '360px',
                background: 'radial-gradient(ellipse at top left, #1e1b4b 0%, #0a0e1a 50%), radial-gradient(ellipse at bottom right, #134e4a 0%, transparent 60%)',
                backgroundColor: '#0a0e1a',
                position: 'relative',
                overflow: 'hidden',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                color: 'white',
              }}
            >
              {/* Decorative corner glows ONLY — the giant center tone glow was
                  bleeding everywhere on Twitter render. Corners only stays clean. */}
              <div style={{ position: 'absolute', top: -120, right: -120, width: 220, height: 220, borderRadius: '50%', background: 'rgba(34,211,238,0.10)', filter: 'blur(60px)' }} />
              <div style={{ position: 'absolute', bottom: -120, left: -120, width: 220, height: 220, borderRadius: '50%', background: 'rgba(168,85,247,0.10)', filter: 'blur(60px)' }} />

              {/* Subtle dot grid texture */}
              <div style={{
                position: 'absolute', inset: 0,
                backgroundImage: 'radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px)',
                backgroundSize: '14px 14px',
                pointerEvents: 'none',
              }} />

              {/* Top brand strip — REAL site logo (replaces the placeholder 'S' tile
                  to match the brand identity from the navbar / favicon).
                  Brand text stays solid white so html2canvas captures it cleanly. */}
              <div style={{ position: 'absolute', top: 18, left: 22, right: 22, display: 'flex', alignItems: 'center', justifyContent: 'space-between', zIndex: 2 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  {/* Logo in framed badge — uses the base64 data URI (set on mount)
                      so html2canvas captures the real logo, not a broken-image
                      fallback. Visual treatment matches the navbar. */}
                  <div style={{
                    width: 42, height: 42, borderRadius: 9,
                    background: 'linear-gradient(135deg, rgba(168,85,247,0.18), rgba(34,211,238,0.18))',
                    border: '1.5px solid rgba(34,211,238,0.45)',
                    boxShadow: '0 6px 18px rgba(34,211,238,0.25)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: 5, boxSizing: 'border-box',
                  }}>
                    <img
                      src={logoDataUri}
                      alt=""
                      style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
                    />
                  </div>
                  <span style={{
                    fontWeight: 800, fontSize: 18, letterSpacing: '-0.01em',
                    color: '#ffffff',
                    lineHeight: 1,
                    fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
                  }}>SnapChart <span style={{ color: '#22d3ee' }}>AI</span></span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 9px', borderRadius: 6, background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.30)' }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#10b981', boxShadow: '0 0 8px #10b981' }} />
                  <span style={{ fontSize: 8, fontFamily: 'ui-monospace, monospace', color: '#6ee7b7', letterSpacing: '0.2em', textTransform: 'uppercase', fontWeight: 700, lineHeight: 1 }}>live · 2s verdict</span>
                </div>
              </div>

              {/* Main content — tightened to fit verdict + confidence pill + tagline
                  WITHOUT overlapping the bottom branding row.
                  Top 76 (just below brand strip), bottom 60 (above footer).
                  Inside: flex column, justify-between so spacing adapts to content. */}
              <div style={{ position: 'absolute', top: 76, left: 22, right: 22, bottom: 60, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', zIndex: 2, gap: 10 }}>
                {/* Asset + price row */}
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', padding: '0 2px' }}>
                  <div>
                    <div style={{ fontSize: 9, fontFamily: 'ui-monospace, monospace', color: 'rgba(255,255,255,0.4)', letterSpacing: '0.25em', textTransform: 'uppercase', marginBottom: 2 }}>asset</div>
                    <div style={{ fontFamily: 'ui-monospace, monospace', fontSize: 15, fontWeight: 700, color: 'white', letterSpacing: '0.04em' }}>${verdict.asset || '—'}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 9, fontFamily: 'ui-monospace, monospace', color: 'rgba(255,255,255,0.4)', letterSpacing: '0.25em', textTransform: 'uppercase', marginBottom: 2 }}>price</div>
                    <div style={{ fontFamily: 'ui-monospace, monospace', fontSize: 15, fontWeight: 700, color: 'white' }}>{fmtPrice(verdict.price)}</div>
                  </div>
                </div>

                {/* Hero verdict — tighter padding so the box doesn't crowd the tagline */}
                <div style={{
                  borderRadius: 14, padding: '16px 14px 18px', textAlign: 'center',
                  background: `linear-gradient(180deg, ${tone.hex}1F 0%, transparent 100%)`,
                  border: `2px solid ${tone.hex}40`,
                  boxShadow: `0 18px 50px ${tone.glow}, inset 0 1px 0 rgba(255,255,255,0.05)`,
                  position: 'relative',
                }}>
                  <div style={{ fontSize: 9, fontFamily: 'ui-monospace, monospace', color: 'rgba(255,255,255,0.5)', letterSpacing: '0.3em', textTransform: 'uppercase', marginBottom: 8, fontWeight: 600 }}>
                    ai risk officer says
                  </div>
                  <div style={{
                    fontSize: 46, fontWeight: 900, fontFamily: 'ui-monospace, monospace',
                    color: tone.hex, lineHeight: 1.15, letterSpacing: '-0.02em',
                    textShadow: `0 4px 30px ${tone.glow}`,
                    padding: '2px 0 4px',
                  }}>{v}</div>
                  {Number(verdict.conf) > 0 ? (
                    <div style={{ marginTop: 6, display: 'inline-block', padding: '3px 9px', borderRadius: 5, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
                      <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: 10, color: 'rgba(255,255,255,0.7)', letterSpacing: '0.1em', fontWeight: 600 }}>
                        {verdict.conf}% confidence
                      </span>
                    </div>
                  ) : null}
                </div>

                {/* Tagline — compact 2-line max, smaller font, no wasted vertical space */}
                <div style={{ padding: '0 4px', fontSize: 11.5, color: 'rgba(255,255,255,0.75)', textAlign: 'center', lineHeight: 1.35 }}>
                  <span style={{ fontStyle: 'italic' }}>"Anyone can take a trade.</span><br/>
                  <span style={{ fontStyle: 'italic' }}>Professionals know when </span>
                  <span style={{ color: '#22d3ee', fontWeight: 700, fontStyle: 'normal' }}>NOT</span>
                  <span style={{ fontStyle: 'italic' }}> to."</span>
                </div>
              </div>

              {/* Bottom branding */}
              <div style={{ position: 'absolute', bottom: 18, left: 22, right: 22, display: 'flex', alignItems: 'center', justifyContent: 'space-between', zIndex: 2 }}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: 8, color: 'rgba(255,255,255,0.3)', letterSpacing: '0.2em', textTransform: 'uppercase' }}>shared by</span>
                  <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: 11, color: 'rgba(255,255,255,0.6)', fontWeight: 600 }}>@{userHandle}</span>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12, color: '#22d3ee', fontWeight: 700, letterSpacing: '0.02em' }}>snap-chart.ai</span>
                </div>
              </div>
            </div>
          </div>

          {/* Channel grid — primary share routes (matches ShareAnalysisCard pattern) */}
          <div className="grid grid-cols-5 gap-2 mb-3">
            <ChannelBtn onClick={nativeShare} disabled={busy} label="Share" tone="primary">
              <SafeIcon icon={FiShare2} className="text-xl" />
            </ChannelBtn>
            <ChannelBtn onClick={postOnX} disabled={busy} label="X">
              <span className="font-black text-2xl leading-none">𝕏</span>
            </ChannelBtn>
            <ChannelBtn onClick={postOnInstagram} disabled={busy} label="IG">
              <FaInstagram className="text-xl" />
            </ChannelBtn>
            <ChannelBtn onClick={postOnTiktok} disabled={busy} label="TikTok">
              <FaTiktok className="text-xl" />
            </ChannelBtn>
            <ChannelBtn onClick={postOnWhatsapp} disabled={busy} label="WhatsApp">
              <FaWhatsapp className="text-xl" />
            </ChannelBtn>
          </div>

          {/* Secondary actions */}
          <div className="grid grid-cols-2 gap-2 mb-3">
            <button onClick={copyImage} disabled={busy}
              className="flex items-center justify-center gap-2 py-2.5 rounded-lg border border-white/10 text-gray-200 hover:bg-white/5 hover:border-white/20 transition text-xs font-mono uppercase tracking-[0.15em] disabled:opacity-50">
              <SafeIcon icon={copied ? FiCheck : FiCopy} className="text-base" />
              {copied ? 'image copied' : 'copy image'}
            </button>
            <button onClick={download} disabled={busy}
              className="flex items-center justify-center gap-2 py-2.5 rounded-lg border border-white/10 text-gray-200 hover:bg-white/5 hover:border-white/20 transition text-xs font-mono uppercase tracking-[0.15em] disabled:opacity-50">
              <SafeIcon icon={busy ? FiImage : FiDownload} className="text-base" />
              {busy ? 'rendering…' : 'download'}
            </button>
          </div>

          {/* IG instruction hint */}
          <AnimatePresence>
            {igHint && (
              <motion.div
                initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                className="mb-3 p-3 rounded-lg border border-pink-500/30 bg-pink-500/10 text-xs text-pink-200">
                ✓ Image downloaded · caption copied. Paste it in your IG story or post.
              </motion.div>
            )}
          </AnimatePresence>

          <p className="text-[10px] text-gray-500 font-mono uppercase tracking-[0.15em] text-center">
            1080×1080 · 3x retina · perfect for ig post / story / reel / tiktok / x
          </p>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

function ChannelBtn({ children, label, onClick, disabled, tone = 'default' }) {
  const isPrimary = tone === 'primary';
  return (
    <button onClick={onClick} disabled={disabled}
      className={`flex flex-col items-center justify-center gap-1.5 py-3 rounded-xl transition disabled:opacity-50 ${
        isPrimary
          ? 'bg-gradient-to-br from-cyan-400 to-purple-400 text-black hover:scale-[1.02] shadow-xl shadow-cyan-500/30 font-bold'
          : 'border border-white/10 text-gray-200 hover:bg-white/5 hover:border-cyan-400/40 hover:text-cyan-300'
      }`}>
      {children}
      <span className="text-[9px] font-mono uppercase tracking-[0.15em]">{label}</span>
    </button>
  );
}

export default VerdictShareCard;
